import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi, apiKeysApi } from '../services/projectApi';
import type {
  CreateProjectRequest,
  UpdateProjectRequest,
  CreateApiKeyRequest,
} from '../types/project';

// Query keys
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (organizationId: string) => [...projectKeys.lists(), organizationId] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectKeys.details(), id] as const,
  apiKeys: (projectId: string) => [...projectKeys.detail(projectId), 'api-keys'] as const,
};

// ============ Query Hooks ============

export function useProjects(organizationId: string | undefined) {
  return useQuery({
    queryKey: projectKeys.list(organizationId!),
    queryFn: async () => {
      const response = await projectsApi.list(organizationId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch projects');
      }
      return response.data ?? [];
    },
    enabled: !!organizationId,
  });
}

export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: projectKeys.detail(projectId!),
    queryFn: async () => {
      const response = await projectsApi.get(projectId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch project');
      }
      return response.data!;
    },
    enabled: !!projectId,
  });
}

export function useApiKeys(projectId: string | undefined) {
  return useQuery({
    queryKey: projectKeys.apiKeys(projectId!),
    queryFn: async () => {
      const response = await apiKeysApi.list(projectId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch API keys');
      }
      return response.data ?? [];
    },
    enabled: !!projectId,
  });
}

// ============ Mutation Hooks ============

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      data,
    }: {
      organizationId: string;
      data: CreateProjectRequest;
    }) => {
      const response = await projectsApi.create(organizationId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create project');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.list(variables.organizationId) });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string;
      data: UpdateProjectRequest;
    }) => {
      const response = await projectsApi.update(projectId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update project');
      }
      return response.data!;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.list(data.organization_id) });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
    }: {
      projectId: string;
      organizationId: string;
    }) => {
      const response = await projectsApi.delete(projectId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete project');
      }
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.list(variables.organizationId) });
    },
  });
}

export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string;
      data: CreateApiKeyRequest;
    }) => {
      const response = await apiKeysApi.create(projectId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create API key');
      }
      return { apiKey: response.data!, message: response.message };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.apiKeys(variables.projectId) });
    },
  });
}

export function useRevokeApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      keyId,
    }: {
      projectId: string;
      keyId: string;
    }) => {
      const response = await apiKeysApi.revoke(projectId, keyId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to revoke API key');
      }
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.apiKeys(variables.projectId) });
    },
  });
}
